import { useEffect } from "react";
import { Project } from "@prisma/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";

interface DeleteProjectModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
  onConfirm: () => void;
  isPending: boolean;
}

export function DeleteProjectModal({
  isOpen,
  onOpenChange,
  project,
  onConfirm,
  isPending,
}: DeleteProjectModalProps) {
  const formSchema = z.object({
    name: z.literal(project.name, {
      errorMap: () => {
        return { message: "The name of project to delete must match." };
      },
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  const handleSubmit = () => {
    onConfirm();
  };

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              <strong className="px-1">{project.name}</strong>
              project and all associated data.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive" className="my-4">
            <AlertCircle className="h-4 w-4" />
            {/* <AlertTitle>Warning</AlertTitle> */}
            <AlertDescription>
              To confirm deletion, please type the project name
              <strong className="ml-1 font-mono bg-destructive/20 px-1 py-0.5 rounded">
                {project.name}
              </strong>{" "}
              below.
            </AlertDescription>
          </Alert>

          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-8"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Project Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter project name to confirm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                  </>
                ) : (
                  "Delete Project"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
